import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  GraduationCap,
  Settings,
  SearchCheck,
  Briefcase,
  ArrowRight,
  Send,
  CheckCircle2,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const SERVICES = [
  {
    icon: GraduationCap,
    title: "Bire Bir Personel Eğitimi",
    lead: "Yeni personelinizi işletmenizin sistemine göre yetiştirin.",
    desc: "Personelin genel bir muhasebe eğitimi alması yerine, işletmenizin kullandığı muhasebe/ERP programı ve gerçek iş akışları üzerinden bire bir uygulamalı eğitim verilir.",
    highlight: true,
  },
  {
    icon: Settings,
    title: "Muhasebe Sistem Kurulumu",
    desc: "Cari, stok, fatura, kasa, banka ve muhasebe süreçlerinin işletmeye uygun şekilde yapılandırılması.",
  },
  {
    icon: SearchCheck,
    title: "Süreç & Risk Analizi",
    desc: "Mevcut muhasebe süreçlerinin incelenmesi, aksayan noktaların ve operasyonel risklerin belirlenmesi.",
  },
  {
    icon: Briefcase,
    title: "Dışarıdan Danışmanlık",
    desc: "İşletmenin ihtiyaçlarına göre muhasebe süreçleri ve operasyonları konusunda profesyonel destek.",
  },
];

const ERP_PROGRAMS = ["Logo", "Mikro", "Luca", "Zirve", "DİA", "Orka", "Nebim V3", "Uyumsoft"];

const SERVICE_OPTIONS = [
  "Bire Bir Personel Eğitimi",
  "Muhasebe Sistem Kurulumu",
  "Süreç & Risk Analizi",
  "Dışarıdan Danışmanlık",
  "Diğer",
];

export default function CorporateSolutions() {
  const [form, setForm] = useState({
    company_name: "",
    contact_name: "",
    phone: "",
    email: "",
    employee_count: "",
    erp_program: "",
    service_type: "",
    description: "",
  });
  const [state, setState] = useState({ loading: false, ok: false, error: "" });

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setState({ loading: true, ok: false, error: "" });
    try {
      await api.post("/corporate-leads", form);
      setState({ loading: false, ok: true, error: "" });
      setForm({
        company_name: "",
        contact_name: "",
        phone: "",
        email: "",
        employee_count: "",
        erp_program: "",
        service_type: "",
        description: "",
      });
    } catch (err) {
      setState({ loading: false, ok: false, error: formatApiError(err.response?.data?.detail) || err.message });
    }
  };

  return (
    <div>
      <title>Kurumsal Çözümler — ZB Campus Muhasebe Akademisi</title>
      <meta
        name="description"
        content="İşletmenize özel muhasebe personeli eğitimi, muhasebe sistem kurulumu, süreç & risk analizi ve dışarıdan danışmanlık hizmetleri. ZB Campus Kurumsal Çözümler."
      />

      {/* HERO */}
      <section data-testid="corporate-hero" className="relative overflow-hidden noise-overlay">
        <div className="container-x py-20 md:py-28">
          <span className="section-eyebrow">Kurumsal Çözümler</span>
          <h1 className="font-display text-5xl md:text-6xl mt-4 leading-tight max-w-3xl">
            İşletmeniz İçin <span className="italic gold-gradient-text">Muhasebe Çözümleri</span>.
          </h1>
          <p className="mt-5 text-xl text-white/80 max-w-2xl">
            Doğru sistem. Doğru süreç. Doğru ekip.
          </p>
          <p className="mt-5 text-white/70 max-w-2xl leading-relaxed">
            ZB Campus olarak işletmelerin muhasebe operasyonlarında ihtiyaç duyduğu eğitim, sistem
            kurulumu, süreç analizi ve danışmanlık hizmetlerini işletmeye özel olarak sunuyoruz.
          </p>
        </div>
      </section>

      {/* SERVICES */}
      <section className="container-x py-8 md:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {SERVICES.map((s) => (
            <div
              key={s.title}
              className={`p-8 md:p-10 border ${
                s.highlight ? "border-gold bg-gold/5" : "border-white/10 bg-surface"
              }`}
            >
              <s.icon className={`w-9 h-9 mb-6 ${s.highlight ? "text-gold" : "text-gold/70"}`} />
              <h2 className="font-display text-2xl mb-3">{s.title}</h2>
              {s.lead && <p className="text-white mb-3 font-medium">{s.lead}</p>}
              <p className="text-white/60 leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ERP PROGRAMS */}
      <section className="container-x py-8 md:py-12">
        <div className="border border-white/10 bg-surface p-8 md:p-10">
          <span className="section-eyebrow">Kullanılabilecek Programlar</span>
          <h3 className="font-display text-2xl md:text-3xl mt-3 mb-6">
            Sektörün <span className="italic gold-gradient-text">yaygın kullandığı</span> ERP ve
            muhasebe programları.
          </h3>
          <div className="flex flex-wrap gap-3">
            {ERP_PROGRAMS.map((name) => (
              <span key={name} className="px-5 py-2.5 border border-white/15 text-white/70 text-sm">
                {name}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* LEAD FORM */}
      <section className="container-x py-16 md:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
          <div className="lg:col-span-5">
            <span className="section-eyebrow">Talep Formu</span>
            <h2 className="font-display text-4xl md:text-5xl mt-4 leading-tight">
              Kurumsal <span className="italic gold-gradient-text">Çözüm</span> Talebi.
            </h2>
            <p className="mt-5 text-white/70 max-w-md leading-relaxed">
              Formu doldurun, ekibimiz işletmenizin ihtiyaçlarını değerlendirmek için size 24 saat
              içinde geri dönsün.
            </p>
          </div>

          <div className="lg:col-span-7">
            <form
              onSubmit={submit}
              data-testid="corporate-lead-form"
              className="bg-surface border border-white/10 p-8 md:p-12 space-y-6"
            >
              {state.ok && (
                <div className="flex gap-3 items-start bg-gold/10 border border-gold-30 p-4 text-sm">
                  <CheckCircle2 className="w-5 h-5 text-gold shrink-0 mt-0.5" />
                  <div>Talebiniz iletildi. En kısa sürede size dönüş yapılacaktır.</div>
                </div>
              )}
              {state.error && (
                <div className="bg-red-950/40 border border-red-800 text-red-200 p-4 text-sm">
                  {state.error}
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Field label="Firma Adı" required>
                  <input
                    data-testid="corp-company-name"
                    required
                    value={form.company_name}
                    onChange={(e) => setField("company_name", e.target.value)}
                    className="input-style"
                  />
                </Field>
                <Field label="Yetkili Adı Soyadı" required>
                  <input
                    data-testid="corp-contact-name"
                    required
                    value={form.contact_name}
                    onChange={(e) => setField("contact_name", e.target.value)}
                    className="input-style"
                  />
                </Field>
                <Field label="Telefon" required>
                  <input
                    data-testid="corp-phone"
                    required
                    value={form.phone}
                    onChange={(e) => setField("phone", e.target.value)}
                    className="input-style"
                  />
                </Field>
                <Field label="E-posta" required>
                  <input
                    data-testid="corp-email"
                    type="email"
                    required
                    value={form.email}
                    onChange={(e) => setField("email", e.target.value)}
                    className="input-style"
                  />
                </Field>
                <Field label="Çalışan Sayısı">
                  <input
                    data-testid="corp-employee-count"
                    value={form.employee_count}
                    onChange={(e) => setField("employee_count", e.target.value)}
                    className="input-style"
                  />
                </Field>
                <Field label="Kullanılan Muhasebe / ERP Programı">
                  <input
                    data-testid="corp-erp-program"
                    value={form.erp_program}
                    onChange={(e) => setField("erp_program", e.target.value)}
                    className="input-style"
                  />
                </Field>
              </div>

              <Field label="İhtiyaç Duyulan Hizmet" required>
                <select
                  data-testid="corp-service-type"
                  required
                  value={form.service_type}
                  onChange={(e) => setField("service_type", e.target.value)}
                  className="input-style"
                >
                  <option value="">Seçiniz</option>
                  {SERVICE_OPTIONS.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </Field>

              <Field label="Açıklama">
                <textarea
                  data-testid="corp-description"
                  rows={5}
                  value={form.description}
                  onChange={(e) => setField("description", e.target.value)}
                  className="input-style resize-none"
                />
              </Field>

              <button
                data-testid="corp-submit"
                type="submit"
                disabled={state.loading}
                className="btn-primary disabled:opacity-60"
              >
                {state.loading ? "Gönderiliyor..." : "Kurumsal Görüşme Talep Et"}
                <Send className="w-4 h-4" />
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative overflow-hidden">
        <div className="container-x py-24">
          <div className="border border-gold-30 bg-surface p-10 md:p-16 text-center max-w-3xl mx-auto">
            <span className="section-eyebrow">Bir Sonraki Adım</span>
            <h2 className="font-display text-3xl md:text-4xl mt-4 leading-tight">
              İşletmenizin ihtiyacını <span className="italic gold-gradient-text">birlikte</span>{" "}
              değerlendirelim.
            </h2>
            <p className="mt-5 text-white/70 max-w-xl mx-auto leading-relaxed">
              Standart çözümler yerine, işletmenize özel bir çalışma planı oluşturalım.
            </p>
            <div className="mt-10 flex justify-center">
              <a href="#" onClick={(e) => { e.preventDefault(); document.querySelector('[data-testid="corporate-lead-form"]')?.scrollIntoView({ behavior: "smooth" }); }} className="btn-primary text-sm">
                Kurumsal Görüşme Talep Et <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      <style>{`
        .input-style {
          width: 100%;
          background: #0F0F14;
          border: 1px solid rgba(255,255,255,0.08);
          padding: 0.75rem 1rem;
          color: white;
          font-family: 'Outfit', sans-serif;
          transition: border-color 0.2s ease;
          outline: none;
        }
        .input-style:focus { border-color: rgba(212,175,55,0.6); }
        .input-style::placeholder { color: rgba(255,255,255,0.4); }
      `}</style>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-widest text-white/60 mb-2 inline-block">
        {label} {required && <span className="text-gold">*</span>}
      </span>
      {children}
    </label>
  );
}
